import { mapGetters, mapActions, mapMutations } from 'vuex'
import parseData from '@/utils/parseData'
import languages from '@/utils/_languages.json'
import countries from '@/utils/_countries.json'
import MarkdownEditor from '@/components/MarkdownEditor/MarkdownEditor.vue'
import ManageSubscription from '@/components/ManageSubscription/ManageSubscription.vue'

const MAX_IMAGE_UPLOAD_REQUEST_BYTES = 1700 * 1024
const IMAGE_QUALITY_STEPS = [0.82, 0.72, 0.62, 0.52, 0.42, 0.36]
const IMAGE_SCALE_STEPS = [1, 0.9, 0.8, 0.7, 0.6, 0.5]
const MAX_IMAGE_DIMENSION = 2800

export default {
  name: 'ContentplanSettings',
  data () {
    return {
      isLoading: {
        contentplan: false,
        integrations: false,
        saving: false,
        inviting: false,
        users: false,
        autoblog: false,
        autoblogSaving: false,
        autoblogUpdateMain: false,
        autoblogUpdateStaging: false,
        autoblogSetup: false,
        images: false,
        products: false,
        stripePortal: false,
        documents: false,
        documentUploading: false,
        ecomSetup: false,
        updating: false,
        resetting: false,
        regeneratingAbout: false,
        uploadSchedule: false,
        clusterer: false
      },
      showClusterConfirmationDialog: false,
      confirmationData: null,
      activeTab: 'general',
      tabs: [
        { id: 'general', label: 'General', icon: 'mdi-cog' },
        { id: 'integrations', label: 'Integrations', icon: 'mdi-connection' },
        { id: 'info', label: 'Company info', icon: 'mdi-robot' },
        { id: 'images', label: 'Images', icon: 'mdi-image' },
        { id: 'translations', label: 'Translations', icon: 'mdi-translate' },
        { id: 'content-uploads', label: 'Content uploads', icon: 'mdi-calendar-clock' },
        { id: 'documents', label: 'Documents', icon: 'mdi-file-document' },
        { id: 'users', label: 'Users', icon: 'mdi-account-group' },
        // { id: 'subscription', label: 'Subscription', icon: 'mdi-briefcase-outline' },
        { id: 'bulk-actions', label: 'Bulk actions', icon: 'mdi-check-all' }
      ],
      products: [],
      freeProduct: {
        id: 'free',
        name: 'Free',
        description: 'Free tier with basic features',
        price: {
          formatted: '0€',
          amount: 0
        },
        integrations: {
          gsc: { included: false, formatted: 'Not included' },
          cms: { included: false, formatted: 'Not included' },
          images: { included: false, formatted: 'Not included' }
        },
        autoUpdates: false,
        metadata: {
          monthly_article_credits: '3',
          user_seats: '1'
        }
      },
      settings: [
        { label: 'Name', id: 'name', value: '', disabled: false },
        { label: 'Target country', id: 'country_crawl', value: '', options: [...countries], disabled: false },
        { label: 'Content language', id: 'language', value: '', options: [...languages], disabled: false },
        { label: 'Content plan type', id: 'type', value: '', options: [{ name: 'Standard', code: 'standard' }, { name: 'Ecommerce', code: 'ecommerce' }], disabled: false },
        { label: 'Minimum SERP overlap for clustering', id: 'minimum_score', value: '', options: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10], disabled: false }
      ],

      textFields: [
        { label: 'About your company', id: 'about', value: '', disabled: false, placeholder: 'What does your company provide, for whom and what makes you different?' },
        { label: 'Style guide', id: 'style_guide', value: '', disabled: false, placeholder: 'What would you like your content to look like? Formatting, tone, etc.' },
        { label: 'Image style guide', id: 'image_style_guide', value: '', disabled: false, placeholder: 'What style should images have? Describe format, colors, style guidelines, etc.' }
      ],
      imageSource: 'gemini',
      imageSourceOptions: [
        { label: 'No featured image', value: 'none' },
        { label: 'Gemini', value: 'gemini' },
        { label: 'Flux', value: 'flux' },
        { label: 'OpenAI', value: 'openai' }
      ],

      integrationsAnalytics: [
        { label: 'Google Search Console', id: 'search_console', value: false, disabled: false, route: 'SearchConsoleIntegration' },
        { label: 'Google Analytics', id: 'google_analytics', value: false, disabled: false, route: 'GoogleAnalyticsIntegration' },
        { label: 'BigQuery (GA4 export)', id: 'bigquery', value: false, disabled: false, route: 'BigQueryIntegration' }
      ],

      integrationsCMS: [
        { id: 'wordpress', label: 'WordPress', disabled: false, route: 'WordpressIntegration' },
        { id: 'webflow', label: 'Webflow', disabled: false, route: 'WebflowIntegration' },
        { id: 'shopify', label: 'Shopify', disabled: false, route: 'ShopifyIntegration' },
        { id: 'magento', label: 'Magento', disabled: false, route: 'MagentoIntegration' },
        { id: 'gecko-cms', label: 'Gecko CMS', disabled: false, route: 'GeckoCmsIntegration' },
        { id: 'custom-cms', label: 'Custom CMS', disabled: false, route: 'CustomCmsIntegration' }
      ],
      dialog: false,
      files: {
        lightLogo: null,
        darkLogo: null,
        cssFile: null
      },
      newImages: [],
      images: [],
      imagePreviewDialog: false,
      selectedImage: null,
      copiedField: null,
      deletingImageId: null,
      newUserEmail: '',
      inviteDialog: false,
      selectedCmsIntegration: null,
      showSubscriptionDialog: false,
      documents: [],
      newDocuments: [],
      documentUploadDialog: false,
      translationConfig: {
        original: {
          lang: '',
          domain: '',
          path: ''
        },
        translations: []
      },
      translationStyleGuide: '',
      uploadSchedule: {
        cadence: 'monthly',
        customIntervalDays: null,
        pagesPerUpload: 1,
        daysOfWeek: [],
        daysOfMonth: [1]
      },
      uploadScheduleLastRunAt: null,
      uploadScheduleCron: null,
      uploadCadenceOptions: [
        { label: 'Daily', value: 'daily' },
        { label: 'Weekly', value: 'weekly' },
        { label: 'Monthly', value: 'monthly' },
        { label: 'Custom', value: 'custom' }
      ],
      uploadWeekdayOptions: [
        { label: 'M', value: 1 },
        { label: 'T', value: 2 },
        { label: 'W', value: 3 },
        { label: 'T', value: 4 },
        { label: 'F', value: 5 },
        { label: 'S', value: 6 },
        { label: 'S', value: 7 }
      ],
      uploadMonthDayOptions: Array.from({ length: 31 }, (_, idx) => idx + 1),
      selectedLanguage: null,
      availableLanguages: languages.filter(lang => !lang.code.startsWith('xx-'))
    }
  },
  computed: {
    ...mapGetters([
      'activeContentplan',
      'activeWorkspace',
      'activeIntegration',
      'googleAnalyticsIntegration',
      'bigQueryIntegration',
      'searchConsoleIntegration',
      'wordpressIntegration',
      'webflowIntegration',
      'notionIntegration',
      'shopifyIntegration',
      'customCmsIntegration',
      'magentoIntegration',
      'workspaceUsers',
      'autoblogSettings'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    contentplanName () {
      return this.activeContentplan ? this.activeContentplan.name : ''
    },
    notionUrl () {
      const redirectUri = encodeURIComponent(`${window.location.origin}/notion-integration`)
      const state = encodeURIComponent(`contentplanId=${this.contentplanId.toString()}`)
      return `https://api.notion.com/v1/oauth/authorize?client_id=170d872b-594c-8000-94e9-0037491c0013&response_type=code&owner=user&redirect_uri=${redirectUri}&state=${state}`
    },
    autoblogMainUrl () {
      return this.autoblogSettings.link_main || ''
    },
    autoblogStagingUrl () {
      return this.autoblogSettings.link_staging || ''
    },
    selectedCmsIntegrationValue () {
      return this.selectedCmsIntegration?.value || this.selectedCmsIntegration
    },
    cmsOptions () {
      const options = [
        { label: 'None', value: 'none' },
        { label: 'Gecko CMS', value: 'gecko-cms' }
      ]
      if (this.wordpressIntegration?.type === 'wp-plugin' || this.wordpressIntegration?.wp_domain) {
        options.push({ label: 'WordPress', value: 'wordpress' })
      }
      if (this.webflowIntegration?.wf_site_id) {
        options.push({ label: 'Webflow', value: 'webflow' })
      }
      if (this.shopifyIntegration?.shopify_store) {
        options.push({ label: 'Shopify', value: 'shopify' })
      }
      if (this.customCmsIntegration?.custom_cms_api_base_url) {
        options.push({ label: 'Custom CMS', value: 'custom-cms' })
      }
      if (this.magentoIntegration?.magento_api_base_url) {
        options.push({ label: 'Magento', value: 'magento' })
      }
      if (this.autoblogSettings?.amplify_id) {
        options.push({ label: 'Autoblog', value: 'autoblog' })
      }
      return options
    },
    allProducts () {
      return [this.freeProduct, ...this.products]
    },
    currentTier () {
      return this.activeWorkspace && this.activeWorkspace.managed_tier
        ? this.activeWorkspace.managed_tier
        : 'free'
    },
    currentProduct () {
      if (this.currentTier === 'free') {
        return this.freeProduct
      }

      // Find product by tier metadata or by matching tier name
      return this.products.find(product => {
        const tierFromMetadata = product.metadata?.tier
        if (tierFromMetadata) {
          return tierFromMetadata === this.currentTier
        }
        // Fallback to name matching (convert to lowercase)
        return product.name.toLowerCase() === this.currentTier.toLowerCase()
      }) || this.freeProduct
    },
    filteredLanguages () {
      const usedLangs = this.translationConfig.translations.map(t => t.lang)
      if (this.translationConfig.original.lang) {
        usedLangs.push(this.translationConfig.original.lang)
      }
      return this.availableLanguages.filter(lang => !usedLangs.includes(lang.code))
    }
  },
  methods: {
    parseData,
    ...mapActions([
      'contentplanGetOne',
      'contentplanPut',
      'getIntegrations',
      'imagesPost',
      'imagesGet',
      'imagesPut',
      'sendWebsocketMessage',
      'postUser',
      'getWorkspaceUsers',
      'postAutoblogIntegration',
      'getAutoblogSettings',
      'updateAutoblogSettings',
      'getStripeProducts',
      'getStripeLink',
      'uploadDocument',
      'getDocuments',
      'deleteDocument',
      'getAboutInfoAuto',
      'keywordGet'
    ]),
    ...mapMutations([
      'addProgressTracker',
      'setSnackbar'
    ]),
    async loadData () {
      this.isLoading.contentplan = true
      document.title = 'Contentplan settings | ContentGecko'

      try {
        const [contentplanData] = await Promise.all([
          this.contentplanGetOne({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }),
          this.getAutoblogSettings({ contentplanId: this.contentplanId })
        ])
        this.mapSettings(contentplanData)

        // Load initial tab data
        await this.loadTabData(this.activeTab)
      } catch (error) {
        console.error('Error loading data:', error)
      }

      this.isLoading.contentplan = false
    },
    mapSettings (data) {
      this.settings.forEach(setting => {
        if (setting.id === 'type') {
          // Map DB value "managed" to UI value "standard", keep "ecommerce" as is
          setting.value = data[setting.id] === 'managed' ? 'standard' : (data[setting.id] === 'ecommerce' ? 'ecommerce' : 'standard')
        } else {
          setting.value = data[setting.id]
        }
      })
      this.textFields.forEach(textField => {
        textField.value = data[textField.id] || ''
      })
      this.imageSource = data.image_source || 'gemini'
      this.translationStyleGuide = data.translation_style_guide || ''

      if (data.main_cms_integration === 'none') {
        this.selectedCmsIntegration = { label: 'None', value: 'none' }
      } else {
        const mainCmsIntegration = this.integrationsCMS.find(integration => integration.id === data.main_cms_integration)
        this.selectedCmsIntegration = mainCmsIntegration ? { label: mainCmsIntegration.label, value: mainCmsIntegration.id } : null
      }

      // Load translation config
      this.loadTranslationConfig(data)

      this.uploadScheduleCron = data.upload_cron || null
      this.applyCronSchedule(data.upload_cron)
      if (data.upload_pages_per_run !== null && typeof data.upload_pages_per_run !== 'undefined') {
        this.uploadSchedule.pagesPerUpload = data.upload_pages_per_run
      }
      this.uploadScheduleLastRunAt = data.upload_last_run_at || null
    },
    loadTranslationConfig (data) {
      const translationConfigData = data.translation_config

      if (!translationConfigData) {
        this.translationConfig = {
          original: {
            lang: data.language || 'en',
            domain: data.domain || '',
            path: '/blog'
          },
          translations: []
        }
        return
      }

      let config
      if (typeof translationConfigData === 'string') {
        try {
          config = JSON.parse(translationConfigData)
        } catch (error) {
          console.error('Failed to parse translation_config:', error)
          this.translationConfig = {
            original: {
              lang: data.language || 'en',
              domain: data.domain || '',
              path: '/blog'
            },
            translations: []
          }
          return
        }
      } else {
        config = translationConfigData
      }

      this.translationConfig = {
        original: config.original || {
          lang: data.language || 'en',
          domain: data.domain || '',
          path: '/blog'
        },
        translations: config.translations || []
      }
    },
    async saveSettings () {
      this.isLoading.saving = true

      try {
        const params = this.settings.reduce((acc, item) => {
          if (item.id === 'type') {
            // Map UI value "standard" to DB value "managed", keep "ecommerce" as is
            acc[item.id] = item.value === 'standard' ? 'managed' : item.value
          } else {
            acc[item.id] = item.value
          }
          return acc
        }, {})

        params.about = this.textFields[0].value
        params.style_guide = this.textFields[1].value
        params.image_style_guide = this.textFields[2].value
        params.image_source = this.imageSource
        const mainCmsIntegration = this.selectedCmsIntegration?.value || this.selectedCmsIntegration
        if (mainCmsIntegration) params.main_cms_integration = mainCmsIntegration

        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          ...params,
          action: 'update'
        })
        await this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.error(error)
      }

      this.isLoading.saving = false
    },
    async handleConnect (id) {
      if (id === 'google_analytics') {
        this.$router.push({
          name: 'GoogleAnalyticsIntegration',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }
        })
        return
      }

      if (id === 'bigquery') {
        this.$router.push({
          name: 'BigQueryIntegration',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }
        })
        return
      }

      if (id === 'search_console') {
        this.$router.push({
          name: 'SearchConsoleIntegration',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId
          }
        })
      }
    },
    async handleImageUpload () {
      if (!this.newImages?.length) return
      this.isLoading.images = true

      try {
        let successCount = 0
        const failedFiles = []

        for (const file of this.newImages) {
          try {
            const payload = await this.prepareImageUploadPayload(file)
            await this.imagesPost({
              contentplanId: this.contentplanId,
              fileName: payload.fileName,
              contentType: payload.contentType,
              fileContent: payload.fileContent
            })
            successCount += 1
          } catch (error) {
            console.error(`Error uploading image ${file.name}:`, error)
            failedFiles.push({
              fileName: file.name,
              error
            })
          }
        }

        if (successCount > 0) await this.loadImages()
        this.newImages = []

        if (failedFiles.length) {
          const tooLargeCount = failedFiles.filter(f => f.error?.response?.status === 413).length
          const failedMessage = tooLargeCount === failedFiles.length
            ? `${failedFiles.length} image(s) were too large even after optimization.`
            : `Failed to upload ${failedFiles.length} image(s).`
          this.setSnackbar({
            open: true,
            text: failedMessage,
            type: 'error'
          })
        }
      } catch (error) {
        console.error('Error uploading image:', error)
        this.setSnackbar({
          open: true,
          text: error?.response?.status === 413
            ? 'Image is still too large after optimization. Please choose a smaller image.'
            : (error?.response?.data?.message || error?.message || 'Failed to upload image'),
          type: 'error'
        })
      } finally {
        this.isLoading.images = false
      }
    },
    async prepareImageUploadPayload (file) {
      const webpFileName = this.getWebpFileName(file.name)
      const image = await this.loadImageElement(file)
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')
      if (!context) throw new Error('Canvas 2D context is not available')

      const longestSide = Math.max(image.naturalWidth, image.naturalHeight)
      const maxDimensionScale = longestSide > MAX_IMAGE_DIMENSION ? (MAX_IMAGE_DIMENSION / longestSide) : 1

      for (const scale of IMAGE_SCALE_STEPS) {
        const targetWidth = Math.max(1, Math.floor(image.naturalWidth * maxDimensionScale * scale))
        const targetHeight = Math.max(1, Math.floor(image.naturalHeight * maxDimensionScale * scale))
        canvas.width = targetWidth
        canvas.height = targetHeight
        context.clearRect(0, 0, targetWidth, targetHeight)
        context.drawImage(image, 0, 0, targetWidth, targetHeight)

        for (const quality of IMAGE_QUALITY_STEPS) {
          const fileContent = canvas.toDataURL('image/webp', quality)
          if (!fileContent.startsWith('data:image/webp;base64,')) throw new Error('Browser does not support WebP encoding')
          const requestBytes = this.getImageUploadRequestBytes(webpFileName, fileContent)
          if (requestBytes <= MAX_IMAGE_UPLOAD_REQUEST_BYTES) {
            return {
              fileName: webpFileName,
              contentType: 'image/webp',
              fileContent
            }
          }
        }
      }

      throw new Error('Image is too large to upload. Please choose a smaller image.')
    },
    getImageUploadRequestBytes (fileName, fileContent) {
      const payload = JSON.stringify({
        fileName,
        contentType: 'image/webp',
        fileContent
      })
      return new Blob([payload]).size
    },
    getWebpFileName (fileName) {
      if (!fileName.includes('.')) return `${fileName}.webp`
      return `${fileName.substring(0, fileName.lastIndexOf('.'))}.webp`
    },
    async loadImageElement (file) {
      const imageUrl = URL.createObjectURL(file)
      try {
        return await new Promise((resolve, reject) => {
          const image = new Image()
          image.onload = () => resolve(image)
          image.onerror = () => reject(new Error('Failed to load image'))
          image.src = imageUrl
        })
      } finally {
        URL.revokeObjectURL(imageUrl)
      }
    },
    async loadImages () {
      this.isLoading.images = true
      try {
        const images = await this.imagesGet({
          contentplanId: this.contentplanId
        })
        this.images = images
      } catch (error) {
        console.error('Error loading images:', error)
      }
      this.isLoading.images = false
    },
    viewImage (image) {
      this.selectedImage = image
      this.imagePreviewDialog = true
    },
    async copyToClipboard (text, label) {
      try {
        await navigator.clipboard.writeText(text)
        this.$store.commit('addSnackbar', {
          message: `${label} copied to clipboard`,
          color: 'success'
        })
        this.copiedField = label
        setTimeout(() => {
          this.copiedField = null
        }, 2000)
      } catch (error) {
        console.error('Failed to copy to clipboard:', error)
        this.$store.commit('addSnackbar', {
          message: 'Failed to copy to clipboard',
          color: 'error'
        })
      }
    },
    async handleImageDelete (image) {
      this.deletingImageId = image.id
      try {
        await this.imagesPut({
          contentplanId: this.contentplanId,
          action: 'delete',
          currentName: image.name
        })
        const index = this.images.findIndex(img => img.id === image.id)
        if (index !== -1) {
          this.images.splice(index, 1)
        }
        if (this.selectedImage && this.selectedImage.id === image.id) {
          this.imagePreviewDialog = false
          this.selectedImage = null
        }
      } catch (error) {
        console.error('Error deleting image:', error)
      }
      this.deletingImageId = null
    },
    async loadDocuments () {
      this.isLoading.documents = true
      try {
        const documents = await this.getDocuments({
          contentplanId: this.contentplanId
        })
        this.documents = documents.map(doc => ({
          ...doc,
          statusIcon: this.getDocumentStatusIcon(doc.upload_status),
          statusColor: this.getDocumentStatusColor(doc.upload_status)
        }))
      } catch (error) {
        console.error('Error loading documents:', error)
      }
      this.isLoading.documents = false
    },

    async handleDocumentUpload () {
      if (!this.newDocuments || this.newDocuments.length === 0) return

      this.isLoading.documentUploading = true

      for (const file of this.newDocuments) {
        try {
          await this.uploadDocument({
            contentplanId: this.contentplanId,
            fileName: file.name,
            contentType: file.type,
            fileSize: file.size,
            file
          })
        } catch (error) {
          console.error('Error uploading document:', error)
        }
      }

      await this.loadDocuments()
      this.newDocuments = []
      this.documentUploadDialog = false
      this.isLoading.documentUploading = false
    },

    async handleDocumentDelete (document) {
      if (!confirm(`Are you sure you want to delete "${document.original_filename}"?`)) return

      this.isLoading.documents = true
      try {
        await this.deleteDocument({
          contentplanId: this.contentplanId,
          documentId: document.id
        })
        await this.loadDocuments()
      } catch (error) {
        console.error('Error deleting document:', error)
        this.isLoading.documents = false
      }
    },

    openDocument (document) {
      if (!document.public_url) return
      window.open(document.public_url, '_blank')
    },

    getDocumentStatusIcon (status) {
      switch (status) {
        case 'ready': return 'mdi-check-circle'
        case 'processing': return 'mdi-clock-outline'
        case 'error': return 'mdi-alert-circle'
        case 'uploading': return 'mdi-upload'
        default: return 'mdi-file'
      }
    },

    getDocumentStatusColor (status) {
      switch (status) {
        case 'ready': return 'green'
        case 'processing': return 'orange'
        case 'error': return 'red'
        case 'uploading': return 'blue'
        default: return 'grey'
      }
    },

    formatFileSize (bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    async runUpdating () {
      this.isLoading.updating = true
      try {
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          name: 'Updating contentplan',
          stepsObject: {
            Importing: 'inProgress',
            'Checking new pages': 'pending',
            'Page matching': 'pending',
            'Crawling pages': 'pending'
          }
        })
        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            runUpdating: true,
            userType: 'managed'
          }
        })
      } catch (error) {
        console.error('Error updating contentplan:', error)
      }
      this.isLoading.updating = false
    },
    async runEcomSetup () {
      this.isLoading.ecomSetup = true
      try {
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          name: 'E-commerce setup',
          stepsObject: {
            'Assigning category keywords': 'inProgress',
            'Assigning informational page keywords': 'pending',
            'Assigning articles to topics': 'pending'
          }
        })
        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            runEcomSetup: true,
            userType: 'managed'
          }
        })
      } catch (error) {
        console.error('Error running e-commerce setup:', error)
      }
      this.isLoading.ecomSetup = false
    },
    async updateAutoblog (branch) {
      const loadingKey = branch === 'main' ? 'autoblogUpdateMain' : 'autoblogUpdateStaging'
      this.isLoading[loadingKey] = true
      try {
        await this.postAutoblogIntegration({ contentplanId: this.contentplanId, branch })
      } catch (error) {
        console.error('Error running autoblog:', error)
      }
      this.isLoading[loadingKey] = false
    },
    async resetClusters () {
      if (!confirm('Are you sure you want to reset all clusters? This action cannot be undone.')) return

      this.isLoading.resetting = true
      try {
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          action: 'resetClusters'
        })
      } catch (error) {
        console.error('Error resetting clusters:', error)
      }
      this.isLoading.resetting = false
    },
    async loadUsers () {
      this.isLoading.users = true
      try {
        await this.getWorkspaceUsers({ workspaceId: this.workspaceId })
      } catch (error) {
        console.error('Error loading users:', error)
      }
      this.isLoading.users = false
    },
    async inviteUser () {
      if (!this.newUserEmail) return

      this.isLoading.inviting = true
      try {
        await this.postUser({
          email: this.newUserEmail,
          workspaceId: this.workspaceId
        })
        await this.loadUsers()
        this.newUserEmail = ''
        this.inviteDialog = false
      } catch (error) {
        console.error('Error inviting user:', error)
      }
      this.isLoading.inviting = false
    },
    async loadAutoblogSettings () {
      this.isLoading.autoblog = true
      try {
        await this.getAutoblogSettings({ contentplanId: this.contentplanId })
      } catch (error) {
        console.error('Error loading autoblog settings:', error)
      }
      this.isLoading.autoblog = false
    },
    async saveAutoblogSettings () {
      this.isLoading.autoblogSaving = true
      try {
        // Use the Vuex action instead of direct fetch
        await this.updateAutoblogSettings({
          contentplanId: this.contentplanId,
          settings: this.autoblogSettings
        })
      } catch (error) {
        console.error('Error saving autoblog settings:', error)
      }
      this.isLoading.autoblogSaving = false
    },
    async saveUploadSchedule () {
      this.isLoading.uploadSchedule = true

      try {
        const pagesPerUpload = parseInt(this.uploadSchedule.pagesPerUpload, 10)
        const cron = this.buildCronSchedule()

        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          upload_cron: cron,
          upload_pages_per_run: Number.isNaN(pagesPerUpload) ? null : pagesPerUpload,
          action: 'update'
        })

        await this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
        this.uploadScheduleCron = cron
      } catch (error) {
        console.error('Error saving upload schedule settings:', error)
      }

      this.isLoading.uploadSchedule = false
    },
    async clearUploadSchedule () {
      this.isLoading.uploadSchedule = true

      try {
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          upload_cron: null,
          upload_pages_per_run: null,
          action: 'update'
        })

        await this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
        this.uploadScheduleCron = null
      } catch (error) {
        console.error('Error clearing upload schedule settings:', error)
      }

      this.isLoading.uploadSchedule = false
    },
    async loadTabData (tabId) {
      switch (tabId) {
        case 'integrations':
          this.isLoading.integrations = true
          try {
            await this.getIntegrations({ contentplanId: this.contentplanId })
          } finally {
            this.isLoading.integrations = false
          }
          break
        case 'users':
          await this.loadUsers()
          break
        case 'images':
          await this.loadImages()
          break
        case 'documents':
          await this.loadDocuments()
          break
        case 'autoblog':
          await this.loadAutoblogSettings()
          break
        case 'subscription':
          await this.loadProducts()
          break
        // Add cases for other tabs if they need specific data loading
        default:
          break
      }
    },
    async loadProducts () {
      // Only load products if they haven't been loaded yet
      if (this.products.length > 0) return
      if (this.isLoading.products) return

      this.isLoading.products = true
      try {
        const response = await this.getStripeProducts()
        if (response?.products) {
          // Filter out mini tier products - ContentplanSettings should only show managed tier products
          // Only show ContentGecko Main products (those with priceId)
          this.products = response.products.filter(product => {
            const tier = product.tier || product.metadata?.tier
            return tier !== 'mini' && product.priceId // Only show main products with priceId
          })
        }
      } catch (error) {
        console.error('Error loading products:', error)
      }
      this.isLoading.products = false
    },
    getMonthlyArticles (product) {
      const credits = product.metadata?.monthly_article_credits || 'Contact us'
      return credits === '999' ? 'Unlimited' : credits
    },
    getUserSeats (product) {
      const seats = product.metadata?.user_seats || '1 seat'
      return seats === '99' ? 'Unlimited' : seats
    },
    getProductIntegrations (product) {
      return product.integrations || {}
    },
    getIntegrationName (key) {
      const integrationNames = {
        gsc: 'Google Search Console',
        cms: 'CMS Integration',
        images: 'Images'
      }
      return integrationNames[key] || key
    },
    async changeTab (tabId) {
      if (this.activeTab === tabId) return

      this.activeTab = tabId

      // Update the route query parameter without adding a new history entry
      if (this.$route.query.tab !== tabId) {
        this.$router.replace({ query: { ...this.$route.query, tab: tabId } }).catch(() => {})
      }

      await this.loadTabData(tabId)
    },
    getFeatureName (key) {
      const featureNames = {
        cmsIntegration: 'CMS Integration',
        gscIntegration: 'GSC Integration',
        images: 'Images',
        extraDomain: 'Extra Domain',
        extraLanguage: 'Extra Language'
      }
      return featureNames[key] || key
    },
    formatExpiryDate (dateString) {
      if (!dateString) return 'N/A'
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
      } catch (error) {
        console.error('Error formatting date:', error)
        return dateString
      }
    },
    formatUploadRunAt (dateString) {
      if (!dateString) return 'Not run yet'
      try {
        const date = new Date(dateString)
        return date.toLocaleString()
      } catch (error) {
        console.error('Error formatting upload run date:', error)
        return dateString
      }
    },
    parseCsvNumberList (value) {
      if (!value) return []
      if (Array.isArray(value)) return value.map(Number).filter(num => Number.isFinite(num))
      return value
        .toString()
        .split(',')
        .map(item => Number(item.trim()))
        .filter(num => Number.isFinite(num))
    },
    applyCronSchedule (cron) {
      if (!cron || typeof cron !== 'string') {
        this.uploadSchedule.cadence = 'monthly'
        this.uploadSchedule.customIntervalDays = null
        this.uploadSchedule.daysOfWeek = []
        this.uploadSchedule.daysOfMonth = [1]
        return
      }

      const parts = cron.trim().split(/\s+/)
      if (parts.length !== 5) return

      const [, , dayOfMonth, , dayOfWeek] = parts

      if (dayOfWeek !== '*') {
        this.uploadSchedule.cadence = 'weekly'
        this.uploadSchedule.daysOfWeek = this.parseCsvNumberList(dayOfWeek)
        this.uploadSchedule.daysOfMonth = []
        this.uploadSchedule.customIntervalDays = null
        return
      }

      if (dayOfMonth.startsWith('*/')) {
        this.uploadSchedule.cadence = 'custom'
        this.uploadSchedule.customIntervalDays = parseInt(dayOfMonth.replace('*/', ''), 10)
        this.uploadSchedule.daysOfWeek = []
        this.uploadSchedule.daysOfMonth = []
        return
      }

      if (dayOfMonth !== '*') {
        this.uploadSchedule.cadence = 'monthly'
        this.uploadSchedule.daysOfMonth = this.parseCsvNumberList(dayOfMonth)
        this.uploadSchedule.daysOfWeek = []
        this.uploadSchedule.customIntervalDays = null
        return
      }

      this.uploadSchedule.cadence = 'daily'
      this.uploadSchedule.customIntervalDays = null
      this.uploadSchedule.daysOfWeek = []
      this.uploadSchedule.daysOfMonth = []
    },
    buildCronSchedule () {
      const minute = 0
      const hour = 9
      let dayOfMonth = '*'
      let dayOfWeek = '*'

      if (this.uploadSchedule.cadence === 'weekly') {
        const days = this.uploadSchedule.daysOfWeek || []
        if (!days.length) return null
        dayOfWeek = days.join(',')
      } else if (this.uploadSchedule.cadence === 'monthly') {
        const days = this.uploadSchedule.daysOfMonth || []
        if (!days.length) return null
        dayOfMonth = days.join(',')
      } else if (this.uploadSchedule.cadence === 'custom') {
        const interval = parseInt(this.uploadSchedule.customIntervalDays, 10)
        if (!Number.isFinite(interval) || interval <= 0) return null
        dayOfMonth = `*/${interval}`
      } else if (this.uploadSchedule.cadence !== 'daily') {
        return null
      }

      return `${minute} ${hour} ${dayOfMonth} * ${dayOfWeek}`
    },
    formatCronSummary (cron) {
      const parts = (cron || '').trim().split(/\s+/)
      if (parts.length !== 5) return 'Not scheduled'

      const [minute, hour, dayOfMonth, , dayOfWeek] = parts
      const time = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} UTC`

      if (dayOfWeek !== '*') {
        const days = this.parseCsvNumberList(dayOfWeek).map(day => this.formatWeekdayLabel(day))
        return `Weekly on ${days.join(', ')} at ${time}`
      }

      if (dayOfMonth.startsWith('*/')) {
        const interval = parseInt(dayOfMonth.replace('*/', ''), 10)
        return `Every ${interval} days at ${time}`
      }

      if (dayOfMonth !== '*') {
        const days = this.parseCsvNumberList(dayOfMonth)
        return `Monthly on ${days.join(', ')} at ${time}`
      }

      return `Daily at ${time}`
    },
    formatWeekdayLabel (day) {
      const labels = {
        1: 'Mon',
        2: 'Tue',
        3: 'Wed',
        4: 'Thu',
        5: 'Fri',
        6: 'Sat',
        7: 'Sun'
      }
      return labels[day] || day.toString()
    },
    async openStripePortal () {
      this.isLoading.stripePortal = true
      try {
        const response = await this.getStripeLink({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
        if (response?.url) {
          window.open(response.url, '_blank')
        }
      } catch (error) {
        console.error('Error opening Stripe portal:', error)
      }
      this.isLoading.stripePortal = false
    },
    async regenerateAboutInfo () {
      this.isLoading.regeneratingAbout = true
      try {
        const response = await this.getAboutInfoAuto({
          contentplanId: this.contentplanId,
          regenerate: true
        })
        if (response?.about) {
          this.textFields[0].value = response.about
        }
        await this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.error('Error regenerating about info:', error)
      }
      this.isLoading.regeneratingAbout = false
    },
    addTranslationLanguage (language) {
      if (!language) return

      const langCode = language.code
      if (this.translationConfig.translations.some(t => t.lang === langCode)) return
      if (this.translationConfig.original.lang === langCode) return

      this.translationConfig.translations.push({
        lang: langCode,
        domain: '',
        path: '/blog'
      })
      this.selectedLanguage = null
    },
    removeTranslationLanguage (index) {
      this.translationConfig.translations.splice(index, 1)
    },
    getLanguageName (code) {
      const lang = this.availableLanguages.find(l => l.code === code)
      return lang ? lang.name : code.toUpperCase()
    },
    async saveTranslationSettings () {
      this.isLoading.saving = true

      try {
        await this.contentplanPut({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId,
          translation_config: JSON.stringify(this.translationConfig),
          translation_style_guide: this.translationStyleGuide,
          action: 'update'
        })

        await this.contentplanGetOne({
          workspaceId: this.workspaceId,
          contentplanId: this.contentplanId
        })
      } catch (error) {
        console.error('Error saving translation settings:', error)
      }

      this.isLoading.saving = false
    },
    async runClusterer () {
      const keywordsData = await this.keywordGet({
        workspaceId: this.workspaceId,
        contentplanId: this.contentplanId,
        q: '.*',
        limit: 1,
        page: 1,
        status: ['in_plan', 'not_in_plan', 'excluded']
      })

      const activeKeywords = keywordsData?.meta?.counts?.inPlanCount ?? 0
      const inactiveKeywords = keywordsData?.meta?.counts?.notInPlanCount ?? 0
      const planLimit = this.activeContentplan?.keyword_limit ?? 0
      const keywordsToActivate = Math.max(0, Math.min(inactiveKeywords, planLimit - activeKeywords))

      this.confirmationData = {
        activeKeywords,
        inactiveKeywords,
        planLimit,
        keywordsToActivate
      }

      this.showClusterConfirmationDialog = true
    },
    async executeClustering () {
      this.showClusterConfirmationDialog = false
      this.isLoading.clusterer = true
      try {
        this.addProgressTracker({
          contentplanId: this.contentplanId,
          name: 'Adding keywords to clusters',
          stepsObject: {
            Crawling: 'inProgress',
            Clustering: 'pending',
            Labeling: 'pending',
            'Page matching': 'pending'
          }
        })

        await this.sendWebsocketMessage({
          action: 'runOrchestrator',
          message: {
            contentplanId: this.contentplanId,
            runGrouping: true,
            userType: 'managed',
            queueKeywords: true
          }
        })
      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading.clusterer = false
      }
    }
  },
  async mounted () {
    const initialTab = this.$route.query.tab
    if (initialTab && this.tabs.some(tab => tab.id === initialTab)) {
      this.activeTab = initialTab
    }

    await this.loadData()
  },
  components: {
    MarkdownEditor,
    ManageSubscription
  }
}
